import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { inboxAPI, InboxItem } from '../services/inboxAPI';
import { useTheme } from '../contexts/ThemeContext';

// Legacy-markdown-lite: render *bold* (single asterisk, matching the Telegram DM path) as bold,
// and make URLs clickable — so a notification body reads the same on web as it does in Telegram.
const renderBody = (text: string): React.ReactNode =>
  text.split(/(\*[^*\n]+\*|https?:\/\/[^\s]+)/g).map((part, i) => {
    if (part.length > 2 && part.startsWith('*') && part.endsWith('*')) {
      return <strong key={i}>{part.slice(1, -1)}</strong>;
    }
    if (/^https?:\/\//.test(part)) {
      return (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noreferrer"
          style={{ color: '#2f7d31', fontWeight: 700, wordBreak: 'break-all' }}
        >
          {part}
        </a>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });

const formatRelative = (iso: string | null): string => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const seconds = Math.floor((Date.now() - then) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 86400 * 7) return `${Math.floor(seconds / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};

const Inbox: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const C = {
    bg: dark ? '#0f1216' : '#f8fafc',
    card: dark ? '#171b21' : 'white',
    border: dark ? '#262c35' : '#e2e8f0',
    borderStrong: dark ? '#2d333b' : '#cbd5e1',
    unreadBorder: dark ? 'rgba(124,179,255,0.4)' : '#bfdbfe',
    text: dark ? '#e6e8eb' : '#0f172a',
    textStrong: dark ? '#e6e8eb' : '#1e293b',
    muted: dark ? '#aab1ba' : '#64748b',
    backArrow: dark ? '#aab1ba' : '#475569',
    faint: dark ? '#7d8590' : '#94a3b8',
    body: dark ? '#aab1ba' : '#475569',
    dot: dark ? '#7cb3ff' : '#3b82f6',
    errorBg: dark ? 'rgba(255,107,107,0.15)' : '#fef2f2',
    errorText: dark ? '#ff6b6b' : '#991b1b',
    // Filled brand CTA keeps its saturated green so the white label stays
    // legible on dark (a lightened green would wash the label out).
    ctaBg: '#4a7c10',
  };
  const [items, setItems] = useState<InboxItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const load = useCallback(async (cursor?: string) => {
    try {
      const data = await inboxAPI.list({ cursor, limit: 20 });
      setItems(prev => cursor ? [...prev, ...data.items] : data.items);
      setHasMore(data.has_more);
      setNextCursor(data.next_cursor);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not load inbox.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleExpand = async (item: InboxItem) => {
    setExpandedId(prev => (prev === item.id ? null : item.id));
    if (!item.read_at) {
      try {
        await inboxAPI.markRead(item.id);
        setItems(prev =>
          prev.map(i => (i.id === item.id ? { ...i, read_at: new Date().toISOString() } : i)),
        );
      } catch {
        /* non-critical */
      }
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await inboxAPI.markAllRead();
      const now = new Date().toISOString();
      setItems(prev => prev.map(i => (i.read_at ? i : { ...i, read_at: now })));
    } catch {
      /* non-critical */
    }
  };

  const handleDismiss = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await inboxAPI.dismiss(id);
      setItems(prev => prev.filter(i => i.id !== id));
    } catch {
      /* non-critical */
    }
  };

  const unreadCount = items.filter(i => !i.read_at).length;

  return (
    <div style={{ minHeight: '100vh', background: C.bg }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '1rem',
          background: C.card,
          borderBottom: `1px solid ${C.border}`,
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'transparent',
            border: 'none',
            padding: '8px 12px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            color: C.backArrow,
          }}
        >
          ← Back
        </button>
        <h1 style={{ margin: 0, marginLeft: 8, fontSize: '1.2rem', flex: 1, color: dark ? C.text : undefined }}>Inbox</h1>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllRead}
            style={{
              background: 'transparent',
              border: `1px solid ${C.borderStrong}`,
              borderRadius: 6,
              padding: '6px 10px',
              cursor: 'pointer',
              fontSize: 13,
              color: C.backArrow,
            }}
          >
            Mark all read
          </button>
        )}
      </header>

      <main style={{ maxWidth: 720, margin: '0 auto', padding: '1rem' }}>
        {loading && <div style={{ padding: 24, color: C.muted }}>Loading…</div>}

        {!loading && error && (
          <div style={{ padding: 16, background: C.errorBg, color: C.errorText, borderRadius: 8 }}>
            {error}
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div
            style={{
              padding: '3rem 1rem',
              textAlign: 'center',
              color: C.muted,
              background: C.card,
              borderRadius: 12,
              border: `1px solid ${C.border}`,
            }}
          >
            <div style={{ fontSize: 36, marginBottom: 8 }}>📬</div>
            <div style={{ fontWeight: 600, marginBottom: 4, color: C.textStrong }}>
              No messages yet
            </div>
            <div style={{ fontSize: 14 }}>
              Admin announcements and milestone messages will land here.
            </div>
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {items.map(item => {
              const isExpanded = expandedId === item.id;
              const isUnread = !item.read_at;
              return (
                <div
                  key={item.id}
                  onClick={() => handleExpand(item)}
                  style={{
                    background: C.card,
                    border: '1px solid ' + (isUnread ? C.unreadBorder : C.border),
                    borderRadius: 10,
                    padding: '12px 14px',
                    cursor: 'pointer',
                    boxShadow: isUnread
                      ? (dark ? '0 1px 0 rgba(124,179,255,0.12)' : '0 1px 0 rgba(59,130,246,0.06)')
                      : 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    {isUnread && (
                      <div
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: C.dot,
                          marginTop: 6,
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                        <strong
                          style={{
                            fontSize: 15,
                            color: C.text,
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.title || '(No subject)'}
                        </strong>
                        <span style={{ fontSize: 12, color: C.faint, flexShrink: 0 }}>
                          {formatRelative(item.sent_at)}
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: 14,
                          color: C.body,
                          marginTop: 4,
                          whiteSpace: isExpanded ? 'pre-wrap' : 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {renderBody(item.body)}
                      </div>
                      {isExpanded && (
                        <div
                          style={{
                            marginTop: 12,
                            display: 'flex',
                            gap: 8,
                            alignItems: 'center',
                          }}
                        >
                          {item.cta_url && (() => {
                            // Relative URLs (e.g. /aptitude-drill/today) route
                            // through React Router; absolute URLs still open
                            // in a new tab as before.
                            const isRelative = item.cta_url.startsWith('/');
                            const commonStyle: React.CSSProperties = {
                              background: C.ctaBg,
                              color: 'white',
                              padding: '6px 12px',
                              borderRadius: 6,
                              fontSize: 13,
                              textDecoration: 'none',
                              border: 'none',
                              cursor: 'pointer',
                            };
                            if (isRelative) {
                              return (
                                <button
                                  type="button"
                                  onClick={e => {
                                    e.stopPropagation();
                                    navigate(item.cta_url!);
                                  }}
                                  style={commonStyle}
                                >
                                  {item.cta_label || 'Open'}
                                </button>
                              );
                            }
                            return (
                              <a
                                href={item.cta_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={commonStyle}
                                onClick={e => e.stopPropagation()}
                              >
                                {item.cta_label || 'Open'}
                              </a>
                            );
                          })()}
                          <button
                            onClick={e => handleDismiss(item.id, e)}
                            style={{
                              background: 'transparent',
                              border: `1px solid ${C.border}`,
                              padding: '6px 12px',
                              borderRadius: 6,
                              fontSize: 13,
                              cursor: 'pointer',
                              color: C.muted,
                              marginLeft: 'auto',
                            }}
                          >
                            Dismiss
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {hasMore && (
              <button
                onClick={() => nextCursor && load(nextCursor)}
                style={{
                  marginTop: 8,
                  padding: 12,
                  background: C.card,
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  cursor: 'pointer',
                  color: C.backArrow,
                }}
              >
                Load more
              </button>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default Inbox;
